import { useEffect, useState } from "react";
import {
  Card,
  Tag,
  Button,
  Space,
  Empty,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Tooltip,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  IssuesCloseOutlined,
  HistoryOutlined,
} from "@ant-design/icons";

import { apiClient } from "../services/api/apiClient";

/* ------------------------------------------------------------------ */
/* Tipos                                                              */
/* ------------------------------------------------------------------ */

type Status =
  | "ok"
  | "pending_review"
  | "reviewed_partial"
  | "reviewed_match"
  | "outdated"
  | "irregularity_new"
  | "irregularity_tracked";

interface VarietyStatus {
  variety_id: number;
  variety_name: string;
  variety_code: string | null;
  licensed: number;
  audited: number;
  delta: number;
  status: Status;
  latest_approval_id: number | null;
  latest_approval_reason: string | null;
  open_ticket_id: number | null;
  open_ticket_code: string | null;
}

interface ReviewPayload {
  audit_id?: number;
  farm_id?: number;
  aggregate_status: string;
  items: VarietyStatus[];
}

interface Props {
  /** scope = 'audit' → /v1/audits/<id>/surface-review/
   *  scope = 'farm'  → /v1/farms/<id>/surface-review/ */
  scope: "audit" | "farm";
  id: number;
  /** Solo admin pinta el card. Si false, no se renderiza. */
  isAdmin: boolean;
}

/* ------------------------------------------------------------------ */
/* Helpers de presentación                                            */
/* ------------------------------------------------------------------ */

const STATUS_META: Record<
  Status,
  { color: string; label: string; icon: React.ReactNode }
> = {
  ok: {
    color: "green",
    label: "OK",
    icon: <CheckCircleOutlined />,
  },
  reviewed_partial: {
    color: "green",
    label: "Aprobada (parcial)",
    icon: <CheckCircleOutlined />,
  },
  reviewed_match: {
    color: "green",
    label: "Aprobada (match)",
    icon: <CheckCircleOutlined />,
  },
  pending_review: {
    color: "gold",
    label: "Pendiente revisión",
    icon: <WarningOutlined />,
  },
  outdated: {
    color: "orange",
    label: "Re-validar",
    icon: <WarningOutlined />,
  },
  irregularity_new: {
    color: "red",
    label: "Irregularidad",
    icon: <ExclamationCircleOutlined />,
  },
  irregularity_tracked: {
    color: "volcano",
    label: "Irregularidad en gestión",
    icon: <IssuesCloseOutlined />,
  },
};

const REASON_LABEL: Record<string, string> = {
  partial_planting: "Plantación parcial",
  measurement_match: "Coincidencia con margen",
  other: "Otro",
};

const fmt = (v: number) =>
  Number(v).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

export const SurfaceValidationCard: React.FC<Props> = ({
  scope,
  id,
  isAdmin,
}) => {
  const [data, setData] = useState<ReviewPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<VarietyStatus | null>(
    null,
  );
  const [ticketTarget, setTicketTarget] = useState<VarietyStatus | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const url =
          scope === "audit"
            ? `/v1/audits/${id}/surface-review/`
            : `/v1/farms/${id}/surface-review/`;
        const r = await apiClient.get<ReviewPayload>(url);
        if (!cancelled) setData(r.data);
      } catch (e: any) {
        if (!cancelled)
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "No se pudo cargar el estado.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, id, isAdmin, reloadKey]);

  if (!isAdmin) return null;

  const renderItem = (it: VarietyStatus) => {
    const meta = STATUS_META[it.status];
    const isApproveable =
      it.status === "pending_review" || it.status === "outdated";
    const isIrregNew = it.status === "irregularity_new";
    const isIrregTracked = it.status === "irregularity_tracked";
    return (
      <div
        key={it.variety_id}
        style={{
          padding: "10px 12px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Space size={6} style={{ marginBottom: 6 }}>
          <Tag color="blue">{it.variety_name}</Tag>
          <Tag color={meta.color} icon={meta.icon}>
            {meta.label}
          </Tag>
          {it.latest_approval_reason && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              ({REASON_LABEL[it.latest_approval_reason] || it.latest_approval_reason})
            </Typography.Text>
          )}
        </Space>
        <div style={{ fontSize: 12, color: "#595959" }}>
          Licenciada: <strong>{fmt(it.licensed)} ha</strong> · Auditada:{" "}
          <strong>{fmt(it.audited)} ha</strong> ·{" "}
          <span
            style={{
              color: it.delta > 0 ? "#cf1322" : it.delta < 0 ? "#fa8c16" : "#52c41a",
              fontWeight: 500,
            }}
          >
            Δ {it.delta >= 0 ? "+" : ""}
            {fmt(it.delta)} ha
          </span>
        </div>
        {(isApproveable || isIrregNew || isIrregTracked) && (
          <div style={{ marginTop: 8 }}>
            <Space size={6} wrap>
              {isApproveable && (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setApproveTarget(it)}
                >
                  {it.status === "outdated" ? "Re-validar" : "Validar superficie"}
                </Button>
              )}
              {isIrregNew && (
                <Button
                  danger
                  size="small"
                  icon={<IssuesCloseOutlined />}
                  onClick={() => setTicketTarget(it)}
                >
                  Abrir incidencia de superficie
                </Button>
              )}
              {isIrregTracked && it.open_ticket_id && (
                <Tooltip title={`Ticket ${it.open_ticket_code} ya abierto`}>
                  <Tag color="volcano" icon={<IssuesCloseOutlined />}>
                    Ticket {it.open_ticket_code}
                  </Tag>
                </Tooltip>
              )}
            </Space>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Card
        size="small"
        title={
          <Space>
            <HistoryOutlined />
            <strong>Validación de superficie</strong>
            {data && (
              <Tag color={STATUS_META.pending_review.color}>
                {data.aggregate_status}
              </Tag>
            )}
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: data?.items?.length ? 0 : 16 }}
      >
        {loading && (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        )}
        {error && !loading && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
        )}
        {!loading && !error && data && data.items.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              scope === "audit"
                ? "Sin snapshot de superficie (audit antigua sin cierre nuevo)."
                : "Sin datos de superficie por variedad."
            }
          />
        )}
        {!loading && !error && data && data.items.length > 0 && (
          <div>{data.items.map(renderItem)}</div>
        )}
      </Card>

      <ApproveModal
        target={approveTarget}
        scope={scope}
        id={id}
        onClose={() => setApproveTarget(null)}
        onSuccess={() => {
          setApproveTarget(null);
          setReloadKey((k) => k + 1);
        }}
      />
      <TicketModal
        target={ticketTarget}
        scope={scope}
        id={id}
        onClose={() => setTicketTarget(null)}
        onSuccess={() => {
          setTicketTarget(null);
          setReloadKey((k) => k + 1);
        }}
      />
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Modal: aprobar superficie                                          */
/* ------------------------------------------------------------------ */

const ApproveModal: React.FC<{
  target: VarietyStatus | null;
  scope: "audit" | "farm";
  id: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ target, scope, id, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) {
      form.setFieldsValue({ reason: undefined, notes: "" });
    }
  }, [target, form]);

  const handle = async () => {
    if (!target) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const url =
        scope === "audit"
          ? `/v1/audits/${id}/approve-surface/`
          : `/v1/farms/${id}/approve-surface/`;
      await apiClient.post(url, {
        variety_id: target.variety_id,
        reason: values.reason,
        notes: values.notes,
      });
      message.success("Aprobación registrada");
      onSuccess();
    } catch (e: any) {
      if (e?.errorFields) return; // validación form
      const detail = e?.response?.data?.detail || e?.message || "Error";
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={!!target}
      onCancel={onClose}
      onOk={handle}
      title={
        target
          ? `Validar superficie — ${target.variety_name}`
          : "Validar superficie"
      }
      confirmLoading={submitting}
      okText="Aprobar"
      cancelText="Cancelar"
      destroyOnClose
    >
      {target && (
        <>
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            Licenciada: <strong>{fmt(target.licensed)} ha</strong> · Auditada:{" "}
            <strong>{fmt(target.audited)} ha</strong> · Δ{" "}
            <strong>{fmt(target.delta)} ha</strong>
          </div>
          <Form form={form} layout="vertical">
            <Form.Item
              name="reason"
              label="Motivo"
              rules={[{ required: true, message: "Selecciona un motivo" }]}
            >
              <Select
                options={[
                  {
                    value: "partial_planting",
                    label: "Plantación parcial (productor no ha plantado todo)",
                  },
                  {
                    value: "measurement_match",
                    label: "Coincidencia con margen de medición",
                  },
                  { value: "other", label: "Otro" },
                ]}
                placeholder="¿Por qué se aprueba esta discrepancia?"
              />
            </Form.Item>
            <Form.Item
              name="notes"
              label="Notas (obligatorias)"
              rules={[
                { required: true, message: "Notas obligatorias" },
                { min: 5, message: "Al menos 5 caracteres" },
              ]}
            >
              <Input.TextArea rows={4} placeholder="Justifica la aprobación..." />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Modal: abrir incidencia de superficie                              */
/* ------------------------------------------------------------------ */

const TicketModal: React.FC<{
  target: VarietyStatus | null;
  scope: "audit" | "farm";
  id: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ target, scope, id, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) {
      form.setFieldsValue({
        description: `Detectada irregularidad de superficie en ${target.variety_name}. Δ = +${fmt(target.delta)} ha (auditada ${fmt(target.audited)} ha, licenciada ${fmt(target.licensed)} ha). Iniciar proceso de regularización: nueva auditoría de la sup. extra y/o nueva parcela declarada.`,
        priority: "medium",
      });
    }
  }, [target, form]);

  const handle = async () => {
    if (!target) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const url =
        scope === "audit"
          ? `/v1/audits/${id}/open-surface-ticket/`
          : `/v1/farms/${id}/open-surface-ticket/`;
      const r = await apiClient.post(url, {
        variety_id: target.variety_id,
        description: values.description,
        priority: values.priority,
      });
      if ((r.data as any)?.already_open) {
        message.info(`Ticket ${(r.data as any).code} ya existía abierto`);
      } else {
        message.success(`Ticket ${(r.data as any).code} abierto`);
      }
      onSuccess();
    } catch (e: any) {
      if (e?.errorFields) return;
      const detail = e?.response?.data?.detail || e?.message || "Error";
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={!!target}
      onCancel={onClose}
      onOk={handle}
      title={
        target
          ? `Abrir incidencia de superficie — ${target.variety_name}`
          : "Abrir incidencia de superficie"
      }
      confirmLoading={submitting}
      okText="Abrir incidencia"
      cancelText="Cancelar"
      destroyOnClose
      width={620}
    >
      {target && (
        <>
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            Licenciada: <strong>{fmt(target.licensed)} ha</strong> · Auditada:{" "}
            <strong>{fmt(target.audited)} ha</strong> ·{" "}
            <span style={{ color: "#cf1322", fontWeight: 600 }}>
              Δ +{fmt(target.delta)} ha
            </span>
          </div>
          <Divider style={{ margin: "8px 0" }} />
          <Form form={form} layout="vertical">
            <Form.Item
              name="priority"
              label="Prioridad"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "low", label: "Baja" },
                  { value: "medium", label: "Media" },
                  { value: "high", label: "Alta" },
                  { value: "critical", label: "Crítica" },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="description"
              label="Descripción"
              rules={[{ required: true, min: 10 }]}
            >
              <Input.TextArea rows={6} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default SurfaceValidationCard;
